/**
 * Core types for the canvas-based widget system
 */

// Re-export discovery types
export * from "./discovery.js";

export type DataType =
  | "string"
  | "number"
  | "object"
  | "array"
  | "boolean"
  | "any";

export interface Position {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

// Enhanced schema system for type-safe widget connections
export interface WidgetIOSchema {
  type: DataType;
  properties?: Record<string, WidgetIOSchema>; // For objects
  items?: WidgetIOSchema; // For arrays
  format?: string; // e.g., 'stock-symbol', 'date', 'percentage'
  constraints?: {
    min?: number;
    max?: number;
    pattern?: string;
    enum?: any[];
  };
  description?: string;
}

export interface WidgetInput {
  id: string;
  name: string;
  type: DataType;
  schema?: WidgetIOSchema; // Optional for backward compatibility
  connected?: string; // Output ID from another widget
  value?: any; // Current input value
  acceptsMultiple?: boolean; // Can accept inputs from multiple sources
}

export interface WidgetOutput {
  id: string;
  name: string;
  type: DataType;
  schema?: WidgetIOSchema; // Optional for backward compatibility
  value: any;
}

export interface WidgetState {
  loading: boolean;
  error?: string;
  lastUpdated?: Date;
  data?: any;
}

export interface DataSourceConfig {
  type:
    | "marketData"
    | "stockQuote"
    | "companyInfo"
    | "historicalData"
    | "impactAnalysis"
    | "aiInsights";
  refreshInterval?: number;
  [key: string]: any; // Additional config based on type
}

export interface VisualizationConfig {
  type: "chart" | "table" | "metric" | "custom";
  [key: string]: any;
}

export interface WidgetConfig {
  title?: string;
  refreshInterval?: number;
  dataSource?: DataSourceConfig;
  visualization?: VisualizationConfig;
  [key: string]: any; // Widget-specific config
}

export interface Widget {
  id: string;
  type: string;
  position: Position;
  size: Size;
  zIndex: number;
  config: WidgetConfig;
  inputs: WidgetInput[];
  outputs: WidgetOutput[];
  state: WidgetState;
}

export interface Connection {
  id: string;
  source: { widgetId: string; outputId: string };
  target: { widgetId: string; inputId: string };
  transformer?: DataTransformer;
}

export interface DataTransformer {
  type: string;
  config?: any;
  transform: (data: any) => any;
}

export interface WidgetGroup {
  id: string;
  name: string;
  widgets: Widget[];
  connections: Connection[];
  // External inputs/outputs that are exposed from the group
  exposedInputs: Array<{
    widgetId: string;
    inputId: string;
    groupInputId: string; // The ID for this input on the group
  }>;
  exposedOutputs: Array<{
    widgetId: string;
    outputId: string;
    groupOutputId: string; // The ID for this output on the group
  }>;
  // Optional runtime metadata
  status?: string;
  params?: Record<string, any>;
  trace?: Array<{ t: number; text: string; tool?: string }>;
}

export interface DiscoveryNode {
  id: string;
  title: string;
  description: string;
  prompt: string;
  position: Position;
  createdAt: Date;
  // Optional anchor to a widget to enable anchor-aware placement and UX
  anchorWidgetId?: string;
  // Whether the user explicitly pinned this node on the canvas (vs living in a rail)
  pinned?: boolean;
}

export type CanvasKind = "market" | "profile" | "custom";

export interface CanvasState {
  id: string;
  name: string;
  key?: string;
  kind?: CanvasKind;
  metadata?: Record<string, any>;
  widgets: Widget[];
  connections: Connection[];
  groups: WidgetGroup[];
  discoveryNodes: DiscoveryNode[];
  viewport: {
    x: number;
    y: number;
    zoom: number;
  };
  gridSettings: {
    enabled: boolean;
    size: number;
  };
  // Zoom-to-group state
  zoomedGroupId?: string;
  breadcrumbs?: Array<{
    id: string;
    name: string;
    type: "canvas" | "group";
  }>;
}

export interface WorkspaceState {
  canvases: CanvasState[];
  activeCanvasId: string;
  savedLayouts: SavedLayout[];
}

export interface SavedLayout {
  id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  canvasState: CanvasState;
  createdAt: Date;
  updatedAt: Date;
}

// Widget Definition for Registry
export interface InputDefinition {
  id: string;
  name: string;
  type: DataType;
  schema?: WidgetIOSchema; // Enhanced schema for type validation
  required?: boolean;
  defaultValue?: any;
  description?: string;
  acceptsMultiple?: boolean; // Can accept inputs from multiple sources
  transformer?: (input: any) => any; // Transform incoming data to expected format
}

export interface OutputDefinition {
  id: string;
  name: string;
  type: DataType;
  schema?: WidgetIOSchema; // Enhanced schema for type validation
  description?: string;
}

export interface ConnectionGroup {
  sourceWidgetType: string;
  description: string;
  connections: Array<{
    from: string; // output ID from source widget
    to: string; // input ID on this widget
  }>;
}

export interface WidgetDefinition {
  type: string;
  name: string;
  description: string;
  category: "market" | "analysis" | "portfolio" | "data" | "custom" | "profile";
  icon: React.ComponentType;
  component: React.ComponentType<WidgetProps>;
  defaultConfig: WidgetConfig;
  defaultSize: Size;
  inputDefinitions: InputDefinition[];
  outputDefinitions: OutputDefinition[];
  connectionGroups?: ConnectionGroup[]; // Optional preset connection patterns
}

export interface WidgetProps {
  widget: Widget;
  onUpdateConfig: (config: Partial<WidgetConfig>) => void;
  onUpdateOutputs: (outputs: Partial<Record<string, any>>) => void;
  onUpdateState: (state: Partial<WidgetState>) => void;
  onUpdateInputs?: (inputs: Partial<Record<string, any>>) => void; // Update input values
  isSelected: boolean;
  canvasMode: CanvasMode;
}

// Canvas modes for responsive behavior
export enum CanvasMode {
  DESKTOP = "desktop",
  TABLET = "tablet",
  MOBILE = "mobile",
  COMPACT = "compact",
}

// Touch gesture interfaces
export interface TouchGestures {
  pan: {
    enabled: boolean;
    sensitivity: number;
    momentum: boolean;
  };
  pinchZoom: {
    enabled: boolean;
    minScale: number;
    maxScale: number;
  };
  tap: {
    selectWidget: boolean;
    openContextMenu: boolean;
  };
  longPress: {
    duration: number;
    action: "move" | "menu" | "edit";
  };
}

// Responsive widget extension
export interface ResponsiveWidget extends Widget {
  responsiveConfig?: {
    minSize: Size;
    breakpoints?: {
      mobile: Size;
      tablet: Size;
      desktop: Size;
    };
    collapsible?: boolean;
    mobileView?: "full" | "compact" | "icon";
  };
}

// Widget capability descriptions for AI agents
export interface WidgetCapabilities {
  description: string;
  useCases: string[];
  compatibleWith: string[]; // Widget types this works well with
  dataRequirements: string[];
  outputCapabilities: string[];
  tags?: string[]; // For search and categorization
}
